import { ClipTask, DefaultColors } from '../defines';
import { v4 as uuid } from 'uuid';
import * as THREE from 'three';
import type { Subscription } from '@pointorama/potree-shared';
import { getAnnotationColorProperty, getAnnotationVisibleProperty, getAnnotationClassProperty, getFilterToggleProperty, getClipMethodProperty, getPointClusterSegmentsProperty } from '@pointorama/potree-shared';

type Segment = {
  pointcloudId: string;
  segmentId: number;
};

type ConstructorArgs = {
  identifier?: string;
  segments?: Segment[];
  visible?: boolean;
  clipTask?: number;
  classification?: number;
  selected?: boolean;
  color?: THREE.Color | null;
};

export class PointCluster {
  identifier: string;
  segments: Segment[];
  visible = true;
  finished: boolean;
  private _assignedClipTask = 0;
  private _actualClipTask = 0;
  classification: number;
  selected = false;
  active = false;
  color: THREE.Color;

  // Reactive property subscriptions
  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _filterToggleSubscription?: Subscription;
  private _clipMethodSubscription?: Subscription;
  private _classificationSubscription?: Subscription;
  private _segmentsSubscription?: Subscription;

  // Filter state (kept in sync by reactive subscriptions)
  private _filterEnabled = false;
  private _clipMethod = 'ISOLATE';

  constructor(args: ConstructorArgs) {
    this.identifier = args.identifier || uuid();
    this.segments = args.segments || [];
    if (args.visible !== undefined) this.visible = args.visible;
    this.finished = false;
    this.assignedClipTask = args.clipTask || ClipTask.NONE;
    this.actualClipTask = this.assignedClipTask;
    this.classification = args.classification || -1;
    this.selected = args.selected || false;
    this.color = args.color || new THREE.Color(DefaultColors.HIGHLIGHT);

    this.loadReactiveProperties(args);
  }

  containsSegment(pointcloudId: string, segmentId: number) {
    return this.segments.some((segment) => segment.pointcloudId === pointcloudId && segment.segmentId === segmentId);
  }

  addSegment(pointcloudId: string, segmentId: number) {
    this.segments.push({ pointcloudId, segmentId });
  }

  removeSegment(pointcloudId: string, segmentId: number) {
    this.segments = this.segments.filter((segment) => segment.pointcloudId !== pointcloudId || segment.segmentId !== segmentId);
  }

  filterSegmentsByPointCloud(pointCloudId: string) {
    let filteredSegments = this.segments.filter((segment) => segment.pointcloudId === pointCloudId);

    if (!filteredSegments.length) {
      // Needed in case a segment in a different pointcloud has a grayscale or isolate cliptask
      filteredSegments = [{ pointcloudId: '-1', segmentId: -1 }];
    }
    return filteredSegments.map((segment) => ({
      identifier: segment.segmentId,
      clipTask: this.actualClipTask,
      classification: this.classification,
      visible: this.visible,
      color: this.color,
    }));
  }

  select() {
    this.selected = true;
    this.actualClipTask = ClipTask.HIGHLIGHT;
  }

  unselect() {
    this.selected = false;
    this.actualClipTask = ClipTask.NONE;
  }

  finish() {
    this.finished = true;
  }

  set assignedClipTask(clipTask: number) {
    this._assignedClipTask = clipTask;
  }

  get assignedClipTask() {
    return this._assignedClipTask;
  }

  set actualClipTask(clipTask: number) {
    this._actualClipTask = clipTask;
  }

  get actualClipTask() {
    if (!this.visible) return ClipTask.SHOW_OUTSIDE;
    if (this.assignedClipTask === 0) return this._actualClipTask;
    else return this.assignedClipTask;
  }

  /**
   * Subscribe to reactive properties (annotation_color, annotation_visible).
   * PointCluster is not a THREE.Object3D — setting fields is sufficient since
   * filterSegmentsByPointCloud() reads them each frame.
   */
  loadReactiveProperties(args: Partial<ConstructorArgs> = {}): void {
    if (!this.identifier) return;

    try {
      this._colorSubscription?.unsubscribe();
      this._visibleSubscription?.unsubscribe();

      const colorProperty = getAnnotationColorProperty(this.identifier);
      if (args.color !== undefined) colorProperty.value = '#' + this.color.getHexString();
      else this.color = new THREE.Color(colorProperty.value);
      this._colorSubscription = colorProperty.subscribeToChanges((value) => {
        this.color = new THREE.Color(value);
      });

      const visibleProperty = getAnnotationVisibleProperty(this.identifier);
      if (args.visible !== undefined) visibleProperty.value = this.visible;
      else this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value) => {
        this.visible = value;
      });

      // --- Filter toggle + clip method (always read, never write) ---
      this._filterToggleSubscription?.unsubscribe();
      this._clipMethodSubscription?.unsubscribe();

      const toggleProperty = getFilterToggleProperty(this.identifier);
      if (toggleProperty) {
        this._filterToggleSubscription = toggleProperty.subscribe((enabled) => {
          this._filterEnabled = enabled;
          this._updateClipTask();
        });
      }

      const clipMethodProperty = getClipMethodProperty(this.identifier);
      if (clipMethodProperty) {
        this._clipMethodSubscription = clipMethodProperty.subscribe((method) => {
          this._clipMethod = method;
          this._updateClipTask();
        });
      }

      this._classificationSubscription?.unsubscribe();
      const classProperty = getAnnotationClassProperty(this.identifier);
      if (args.classification !== undefined) classProperty.value = this.classification;
      else this.classification = classProperty.value;
      this._classificationSubscription = classProperty.subscribeToChanges((value) => {
        this.classification = value;
      });

      // Geometry: segments
      // Only skip initial emission if segments were already set
      const hasSegments = this.segments && this.segments.length > 0;
      this._segmentsSubscription?.unsubscribe();
      const segmentsProp = getPointClusterSegmentsProperty(this.identifier);
      let segInit = false;
      this._segmentsSubscription = segmentsProp.subscribe((value) => {
        if (hasSegments && !segInit) { segInit = true; return; }
        segInit = true;
        if (Array.isArray(value)) {
          this.segments = value;
        }
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  private _updateClipTask() {
    this.assignedClipTask = this._filterEnabled ? this._clipMethodToTask(this._clipMethod) : ClipTask.NONE;
  }

  private _clipMethodToTask(clipMethod: string): number {
    switch (clipMethod) {
      case 'HIGHLIGHT':
        return ClipTask.GRAYSCALE;
      case 'HIDE':
        return ClipTask.SHOW_OUTSIDE;
      case 'ISOLATE':
        return ClipTask.SHOW_INSIDE;
      case 'HEATMAP':
        return ClipTask.HEATMAP;
      default:
        return ClipTask.NONE;
    }
  }

  dispose(): void {
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._filterToggleSubscription?.unsubscribe();
    this._clipMethodSubscription?.unsubscribe();
    this._classificationSubscription?.unsubscribe();
  }
}
